import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Alert, Badge, Button } from '../ui';
import {
  RuntimeProviderAuthRequired,
  getRuntimeProviderLabel,
  getRuntimeSourceLabel,
  providerAuthMatchesOAuthMessage,
} from '../../lib/retrieval-runtime-auth';

type AuthCardStatus = 'idle' | 'waiting' | 'resuming' | 'complete' | 'error';

interface ProviderAuthCardProps {
  auth: RuntimeProviderAuthRequired;
  onAuthenticated: (auth: RuntimeProviderAuthRequired) => Promise<void>;
  popupPollIntervalMs?: number;
  popupClosedGraceMs?: number;
}

function getErrorMessage(error: any): string {
  return (
    error?.response?.data?.detail ||
    error?.message ||
    'Resume failed. Authenticate again, then retry the run.'
  );
}

function getOAuthDeniedMessage(data: any): string {
  const code = String(data?.code || data?.error || '').toLowerCase();
  if (code === 'policy_denied' || code === 'access_denied') {
    return 'Authorization was denied by policy.';
  }
  return 'Authentication did not complete.';
}

export function ProviderAuthCard({
  auth,
  onAuthenticated,
  popupPollIntervalMs = 500,
  popupClosedGraceMs = 1500,
}: ProviderAuthCardProps) {
  const providerLabel = getRuntimeProviderLabel(auth);
  const sourceLabel = getRuntimeSourceLabel(auth);
  const authIdentity = [
    auth.source_type,
    auth.provider,
    auth.authorize_url,
    auth.pending_request_id,
    auth.run_id,
  ].join(':');
  const [status, setStatus] = useState<AuthCardStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const popupRef = useRef<Window | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const authCompletedRef = useRef(false);

  const canAuthenticate = Boolean(auth.authorize_url);
  const isBusy = status === 'waiting' || status === 'resuming';
  const message = auth.message || auth.reason || `${sourceLabel} needs authorization before this run can continue.`;

  const resetCleanup = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    popupRef.current = null;
  }, []);

  useEffect(() => {
    authCompletedRef.current = false;
    setStatus('idle');
    setError(null);
    resetCleanup();
  }, [authIdentity, resetCleanup]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      resetCleanup();
    };
  }, [resetCleanup]);

  const completeAuthentication = useCallback(async () => {
    resetCleanup();
    setStatus('resuming');
    setError(null);
    try {
      await onAuthenticated(auth);
      if (mountedRef.current) {
        setStatus('complete');
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setStatus('error');
        setError(getErrorMessage(err));
      }
    }
  }, [auth, onAuthenticated, resetCleanup]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') !== 'complete' || authCompletedRef.current) return;

    const completionData = {
      type: 'oauth_complete',
      source: params.get('source'),
      source_type: params.get('source'),
      provider: params.get('provider'),
      run_id: params.get('run_id'),
      pending_request_id: params.get('pending_request_id'),
      conversation_id: params.get('conversation_id'),
    };
    if (!providerAuthMatchesOAuthMessage(auth, completionData)) return;

    authCompletedRef.current = true;
    void completeAuthentication();
  }, [auth, completeAuthentication]);

  const handleAuthenticate = useCallback(() => {
    if (!auth.authorize_url) {
      setStatus('error');
      setError('Authentication link is unavailable. Refresh the run and try again.');
      return;
    }

    resetCleanup();
    authCompletedRef.current = false;
    setStatus('waiting');
    setError(null);

    const popup = window.open(
      auth.authorize_url,
      'eliza-provider-oauth',
      'popup=yes,width=720,height=760',
    );

    if (!popup) {
      setStatus('error');
      setError('Popup blocked. Allow popups for this site, then authenticate again.');
      return;
    }

    popupRef.current = popup;

    const handleMessage = (event: MessageEvent) => {
      if (!providerAuthMatchesOAuthMessage(auth, event.data)) return;

      authCompletedRef.current = true;
      if (event.data?.error || event.data?.code === 'policy_denied') {
        resetCleanup();
        setStatus('error');
        setError(getOAuthDeniedMessage(event.data));
        return;
      }

      void completeAuthentication();
    };

    const closedInterval = window.setInterval(() => {
      if (authCompletedRef.current) return;
      if (!popupRef.current?.closed) return;
      // The popup closed. A completion postMessage may still be in flight
      // (OAuth callbacks often close the window before the parent processes
      // the message, and some browsers report `closed` early under COOP).
      // Stop polling and give the message a short grace window; if it never
      // arrives, optimistically resume rather than showing a misleading
      // "cancelled" error -- the resume endpoint is the source of truth, so a
      // genuine cancel surfaces as a real resume failure.
      window.clearInterval(closedInterval);
      window.setTimeout(() => {
        if (authCompletedRef.current || !mountedRef.current) return;
        authCompletedRef.current = true;
        void completeAuthentication();
      }, popupClosedGraceMs);
    }, popupPollIntervalMs);

    window.addEventListener('message', handleMessage);
    cleanupRef.current = () => {
      window.removeEventListener('message', handleMessage);
      window.clearInterval(closedInterval);
      try {
        popupRef.current?.close();
      } catch {
        // Ignore popup cleanup errors from cross-origin windows.
      }
    };
  }, [auth, completeAuthentication, popupPollIntervalMs, resetCleanup]);

  const statusBadge = useMemo(() => {
    if (status === 'complete') return <Badge variant="success" className="text-xs">Connected</Badge>;
    if (status === 'error') return <Badge variant="danger" className="text-xs">Action needed</Badge>;
    if (status === 'resuming') return <Badge variant="default" className="text-xs">Resuming</Badge>;
    if (status === 'waiting') return <Badge variant="default" className="text-xs">Waiting</Badge>;
    return <Badge variant="default" className="text-xs">Auth required</Badge>;
  }, [status]);

  return (
    <div className="mt-3 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-dark-surface-2">
          {status === 'complete' ? (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          ) : status === 'error' ? (
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
          ) : (
            <ShieldCheckIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
              Authenticate with {providerLabel}
            </h3>
            {statusBadge}
          </div>
          <p className="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {message}
          </p>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Source: {sourceLabel}
          </p>

          {error && (
            <Alert variant="error" className="mt-3 text-sm">
              {error}
            </Alert>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="brand"
              size="sm"
              onClick={handleAuthenticate}
              disabled={!canAuthenticate || isBusy}
            >
              {status === 'resuming' ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              )}
              {status === 'resuming' ? 'Resuming run' : `Authenticate with ${providerLabel}`}
            </Button>
            {!canAuthenticate && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                No authorization URL was returned.
              </span>
            )}
            {status === 'waiting' && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Complete authentication in the popup.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
